import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Loader2, Search, Download, ChevronLeft, ChevronRight, CalendarDays,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, XCircle,
  ArrowUpCircle, Clock, Building2, User, Filter, X, Landmark, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => `NGN ${Math.round(Number(n || 0)).toLocaleString()}`;

export default function DailyPayoutsPage() {
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('');
  const [bankFilter, setBankFilter] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ companies: [], banks: [] });
  const [dateMode, setDateMode] = useState('all'); // 'all' | 'single' | 'range'
  const [filterDate, setFilterDate] = useState(''); // date picker inside filters panel

  // Load filter options once
  useEffect(() => {
    superAdminAPI.getPayoutFilters()
      .then(setFilterOptions)
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateMode === 'single') params.target_date = date;
      if (dateMode === 'range') {
        params.target_date = date;
        if (endDate) params.end_date = endDate;
      }
      // 'all' mode: no date params sent → backend returns everything
      if (companyFilter) params.company = companyFilter;
      if (bankFilter) params.bank = bankFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (minAmount) params.min_amount = parseFloat(minAmount);
      if (maxAmount) params.max_amount = parseFloat(maxAmount);
      if (search) params.employee = search;
      const res = await superAdminAPI.getDailyPayouts(params);
      setData(res);
    } catch { toast.error('Failed to load daily payouts'); }
    finally { setLoading(false); }
  }, [date, endDate, dateMode, companyFilter, bankFilter, statusFilter, minAmount, maxAmount, search]);

  useEffect(() => { load(); }, [load]);

  const changeDate = (offset) => {
    if (dateMode === 'all') {
      setDateMode('single');
      setDate(new Date().toISOString().split('T')[0]);
      return;
    }
    const d = new Date(date);
    d.setDate(d.getDate() + offset);
    setDate(d.toISOString().split('T')[0]);
  };

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (companyFilter) c++;
    if (bankFilter) c++;
    if (minAmount || maxAmount) c++;
    if (filterDate) c++;
    if (dateMode === 'range' && endDate) c++;
    return c;
  }, [companyFilter, bankFilter, minAmount, maxAmount, filterDate, dateMode, endDate]);

  const clearAllFilters = () => {
    setCompanyFilter('');
    setBankFilter('');
    setMinAmount('');
    setMaxAmount('');
    setEndDate('');
    setFilterDate('');
    setDateMode('all');
    setSearch('');
    setStatusFilter('all');
  };

  const transactions = data?.transactions || [];

  const exportCSV = () => {
    if (!transactions.length) return;
    const headers = ['Time', 'Date', 'Employee', 'Company', 'Amount', 'Bank', 'Account', 'Account Name', 'Status', 'Running Total', 'Reference', 'Error'];
    const rows = transactions.map(tx => [
      tx.time, tx.created_at?.split(' ')[0] || '', tx.employee_name, tx.company_name, tx.amount,
      tx.bank_name, tx.account_number, tx.account_name, tx.status, tx.running_total,
      tx.reference, tx.failure_reason || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `payouts_${date}${endDate ? '_to_' + endDate : ''}.csv`;
    a.click();
    toast.success('CSV exported');
  };

  const summary = data?.summary || {};
  const trend = data?.daily_trend || [];

  const formatDateLabel = (d) => {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  if (loading && !data) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="daily-payouts-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Payout Tracker</h1>
          <p className="text-sm text-gray-500">
            {dateMode === 'all' ? 'All transfers from the start' : dateMode === 'range' ? `${date} to ${endDate || date}` : formatDateLabel(date)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Date Mode Switcher */}
          <div className="flex bg-gray-100 rounded-lg p-0.5" data-testid="date-mode-switcher">
            {[
              { id: 'all', label: 'All Time' },
              { id: 'single', label: 'Day' },
              { id: 'range', label: 'Range' },
            ].map(m => (
              <button key={m.id} onClick={() => setDateMode(m.id)}
                className={`px-3 py-1 text-[11px] font-medium rounded-md transition-colors ${
                  dateMode === m.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`} data-testid={`date-mode-${m.id}`}>
                {m.label}
              </button>
            ))}
          </div>

          {/* Date pickers (hidden in 'all' mode) */}
          {dateMode !== 'all' && (
            <>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => changeDate(-1)} data-testid="prev-day-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2 px-3 py-1.5 border rounded-lg bg-white">
                <CalendarDays className="w-4 h-4 text-gray-400" />
                <input type="date" value={date} onChange={e => setDate(e.target.value)}
                  className="text-sm font-medium text-gray-900 border-0 outline-none bg-transparent w-[130px]"
                  data-testid="date-picker" />
                {dateMode === 'range' && (
                  <>
                    <span className="text-gray-300">to</span>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                      className="text-sm font-medium text-gray-900 border-0 outline-none bg-transparent w-[130px]"
                      data-testid="end-date-picker" />
                  </>
                )}
              </div>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => changeDate(1)} data-testid="next-day-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="border-gray-200" data-testid="total-txns-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpCircle className="w-4 h-4 text-blue-500" />
              <span className="text-[10px] text-gray-400 uppercase tracking-wider">Total Transfers</span>
            </div>
            <p className="text-lg font-bold">{summary.total_transactions || 0}</p>
            <p className="text-[10px] text-gray-400">{fmt(summary.total_amount)}</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200" data-testid="success-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <span className="text-[10px] text-gray-400 uppercase tracking-wider">Successful</span>
            </div>
            <p className="text-lg font-bold text-emerald-600">{summary.success_count || 0}</p>
            <p className="text-[10px] text-emerald-500">{fmt(summary.success_amount)}</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200" data-testid="failed-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-[10px] text-gray-400 uppercase tracking-wider">Failed</span>
            </div>
            <p className="text-lg font-bold text-red-600">{summary.failed_count || 0}</p>
            <p className="text-[10px] text-red-400">{fmt(summary.failed_amount)}</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200" data-testid="success-rate-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-amber-500" />
              <span className="text-[10px] text-gray-400 uppercase tracking-wider">Success Rate</span>
            </div>
            <p className={`text-lg font-bold ${(summary.success_rate || 0) >= 90 ? 'text-emerald-600' : (summary.success_rate || 0) >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
              {summary.success_rate || 0}%
            </p>
          </CardContent>
        </Card>
        <Card className={`border-2 ${(summary.success_rate || 100) < 50 ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} data-testid="tier-limit-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className={`w-4 h-4 ${(summary.success_rate || 100) < 50 ? 'text-red-500' : 'text-gray-400'}`} />
              <span className="text-[10px] text-gray-400 uppercase tracking-wider">Tier Alert</span>
            </div>
            {(summary.failed_count || 0) > 5 ? (
              <p className="text-xs font-semibold text-red-600">Tier limit likely hit</p>
            ) : (
              <p className="text-xs font-semibold text-emerald-600">Normal</p>
            )}
            <p className="text-[10px] text-gray-400 mt-0.5">{fmt(summary.success_amount)} processed</p>
          </CardContent>
        </Card>
      </div>

      {/* 7-Day Trend */}
      {trend.length > 0 && (
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">7-Day Trend</h3>
            <div className="flex items-end gap-1 h-20">
              {trend.map((d, i) => {
                const maxVal = Math.max(...trend.map(t => t.success_amount + t.failed_amount), 1);
                const total = d.success_amount + d.failed_amount;
                const pct = (total / maxVal) * 100;
                const successPct = total > 0 ? (d.success_amount / total) * 100 : 100;
                const isToday = d.date === date;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1 cursor-pointer"
                    onClick={() => { setDateMode('single'); setDate(d.date); }} data-testid={`trend-bar-${d.date}`}>
                    <div className="w-full flex flex-col justify-end" style={{ height: '60px' }}>
                      <div className="w-full rounded-t overflow-hidden" style={{ height: `${Math.max(pct, 2)}%` }}>
                        <div className="bg-emerald-400 w-full" style={{ height: `${successPct}%` }} />
                        <div className="bg-red-400 w-full" style={{ height: `${100 - successPct}%` }} />
                      </div>
                    </div>
                    <span className={`text-[9px] ${isToday ? 'font-bold text-pink-600' : 'text-gray-400'}`}>
                      {d.date.slice(5)}
                    </span>
                    <span className="text-[8px] text-gray-400">{d.total_count}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-2 text-[9px] text-gray-400">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-400" /> Successful</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-400" /> Failed</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filter Bar */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search employee name..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300"
              data-testid="payouts-search" />
          </div>

          {/* Status Filters */}
          <div className="flex gap-1">
            {[
              { id: 'all', label: 'All', count: summary.total_transactions || 0 },
              { id: 'completed', label: 'Success', count: summary.success_count || 0 },
              { id: 'failed', label: 'Failed', count: summary.failed_count || 0 },
            ].map(f => (
              <button key={f.id} onClick={() => setStatusFilter(f.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  statusFilter === f.id
                    ? f.id === 'failed' ? 'bg-red-100 text-red-700' : f.id === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`} data-testid={`filter-${f.id}`}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Advanced Filters Toggle */}
          <button onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              showFilters || activeFilterCount > 0 ? 'bg-pink-50 border-pink-300 text-pink-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
            }`} data-testid="toggle-filters-btn">
            <Filter className="w-3.5 h-3.5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-pink-600 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">{activeFilterCount}</span>
            )}
            <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>

          {/* Export */}
          <Button variant="outline" size="sm" className="gap-2 h-9 shrink-0 ml-auto" onClick={exportCSV}
            disabled={!transactions.length} data-testid="export-csv-btn">
            <Download className="w-4 h-4" />Export CSV
          </Button>
        </div>

        {/* Expanded Filters Panel */}
        {showFilters && (
          <Card className="border-gray-200 bg-gray-50/50" data-testid="filters-panel">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {/* Date Filter */}
                <div>
                  <label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1.5">
                    <CalendarDays className="w-3 h-3 inline mr-1" />Date
                  </label>
                  <div className="space-y-1.5">
                    <input type="date" value={filterDate} onChange={e => { setFilterDate(e.target.value); setDateMode('single'); setDate(e.target.value); }}
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-pink-300"
                      data-testid="filter-date-picker" />
                    {filterDate && (
                      <button onClick={() => { setFilterDate(''); setDateMode('all'); }}
                        className="text-[10px] text-pink-600 hover:underline">Show all dates</button>
                    )}
                  </div>
                </div>

                {/* Company Filter */}
                <div>
                  <label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1.5">
                    <Building2 className="w-3 h-3 inline mr-1" />Company
                  </label>
                  <select value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-pink-300"
                    data-testid="company-filter">
                    <option value="">All Companies</option>
                    {filterOptions.companies.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {/* Bank Filter */}
                <div>
                  <label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1.5">
                    <Landmark className="w-3 h-3 inline mr-1" />Bank
                  </label>
                  <select value={bankFilter} onChange={e => setBankFilter(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-pink-300"
                    data-testid="bank-filter">
                    <option value="">All Banks</option>
                    {filterOptions.banks.map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>

                {/* Amount Range */}
                <div>
                  <label className="text-[10px] text-gray-400 uppercase tracking-wider font-medium block mb-1.5">Amount Range</label>
                  <div className="flex gap-1.5 items-center">
                    <input type="number" value={minAmount} onChange={e => setMinAmount(e.target.value)}
                      placeholder="Min" className="w-full px-2 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-pink-300"
                      data-testid="min-amount-filter" />
                    <span className="text-gray-300 text-xs">-</span>
                    <input type="number" value={maxAmount} onChange={e => setMaxAmount(e.target.value)}
                      placeholder="Max" className="w-full px-2 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-pink-300"
                      data-testid="max-amount-filter" />
                  </div>
                </div>

                {/* Clear All */}
                <div className="flex items-end">
                  <button onClick={clearAllFilters}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs text-gray-500 hover:text-red-600 transition-colors"
                    data-testid="clear-filters-btn">
                    <X className="w-3.5 h-3.5" />Clear all filters
                  </button>
                </div>
              </div>

              {/* Active Filter Tags */}
              {activeFilterCount > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-gray-200">
                  {filterDate && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-pink-100 text-pink-700 rounded-full text-[10px] font-medium">
                      <CalendarDays className="w-3 h-3" />{filterDate}
                      <X className="w-3 h-3 cursor-pointer hover:text-pink-900" onClick={() => { setFilterDate(''); setDateMode('all'); }} />
                    </span>
                  )}
                  {companyFilter && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-medium">
                      <Building2 className="w-3 h-3" />{companyFilter}
                      <X className="w-3 h-3 cursor-pointer hover:text-blue-900" onClick={() => setCompanyFilter('')} />
                    </span>
                  )}
                  {bankFilter && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[10px] font-medium">
                      <Landmark className="w-3 h-3" />{bankFilter}
                      <X className="w-3 h-3 cursor-pointer hover:text-purple-900" onClick={() => setBankFilter('')} />
                    </span>
                  )}
                  {(minAmount || maxAmount) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-medium">
                      {minAmount && `Min: ${fmt(minAmount)}`}{minAmount && maxAmount && ' - '}{maxAmount && `Max: ${fmt(maxAmount)}`}
                      <X className="w-3 h-3 cursor-pointer hover:text-amber-900" onClick={() => { setMinAmount(''); setMaxAmount(''); }} />
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Transactions Table — scrollable */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-pink-400" /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="payouts-table">
          <div className="max-h-[600px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-3 py-2.5 font-medium text-gray-500 whitespace-nowrap">Date</th>
                  <th className="text-left px-3 py-2.5 font-medium text-gray-500 whitespace-nowrap">Time</th>
                  <th className="text-left px-3 py-2.5 font-medium text-gray-500">Employee</th>
                  <th className="text-left px-3 py-2.5 font-medium text-gray-500 hidden md:table-cell">Company</th>
                  <th className="text-left px-3 py-2.5 font-medium text-gray-500 hidden lg:table-cell">Bank / Account</th>
                  <th className="text-right px-3 py-2.5 font-medium text-gray-500">Amount</th>
                  <th className="text-right px-3 py-2.5 font-medium text-gray-500 hidden md:table-cell">Running Total</th>
                  <th className="text-center px-3 py-2.5 font-medium text-gray-500 w-20">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length > 0 ? transactions.map((tx, idx) => {
                  const isSuccess = tx.status === 'completed' || tx.status === 'processing';
                  const isExpanded = expandedRow === tx.id;
                  const txDate = tx.created_at?.split(' ')[0] || tx.created_at?.split('T')[0] || '';
                  return (
                    <React.Fragment key={tx.id}>
                      <tr className={`border-t cursor-pointer transition-colors ${isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'} ${!isSuccess ? 'bg-red-50/40' : ''}`}
                        onClick={() => setExpandedRow(isExpanded ? null : tx.id)}
                        data-testid={`payout-row-${idx}`}>
                        <td className="px-3 py-2 text-gray-400 font-mono whitespace-nowrap text-[10px]">
                          <CalendarDays className="w-3 h-3 inline mr-1 opacity-40" />{txDate}
                        </td>
                        <td className="px-3 py-2 text-gray-500 font-mono whitespace-nowrap">
                          <Clock className="w-3 h-3 inline mr-1 opacity-40" />{tx.time}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                            <span className="font-medium text-gray-900 truncate max-w-[180px]">{tx.employee_name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 hidden md:table-cell">
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-3 h-3 text-gray-300" />
                            <span className="text-gray-600 truncate max-w-[120px]">{tx.company_name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 hidden lg:table-cell text-gray-500">
                          {tx.bank_name} <span className="text-gray-300">|</span> {tx.account_number}
                        </td>
                        <td className="px-3 py-2 text-right font-bold whitespace-nowrap text-gray-900">
                          {fmt(tx.amount)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400 hidden md:table-cell whitespace-nowrap">
                          {fmt(tx.running_total)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            isSuccess ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {isSuccess ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            {tx.status}
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-t border-dashed bg-gray-50">
                          <td colSpan={8} className="px-4 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                              <div>
                                <span className="text-gray-400 block mb-0.5">Reference</span>
                                <span className="font-mono text-gray-700">{tx.reference}</span>
                              </div>
                              <div>
                                <span className="text-gray-400 block mb-0.5">Recipient</span>
                                <span className="text-gray-700">{tx.account_name} - {tx.bank_name} ({tx.account_number})</span>
                              </div>
                              <div>
                                <span className="text-gray-400 block mb-0.5">Date & Time</span>
                                <span className="text-gray-700">{tx.created_at}</span>
                              </div>
                              {tx.failure_reason && (
                                <div className="md:col-span-3">
                                  <span className="text-gray-400 block mb-0.5">Error</span>
                                  <span className="text-red-600 font-medium">{tx.failure_reason}</span>
                                </div>
                              )}
                              {tx.mfb_raw_response && (
                                <div className="md:col-span-3">
                                  <span className="text-gray-400 block mb-0.5">Raw MFB Response</span>
                                  <code className="text-[10px] bg-gray-100 px-2 py-1 rounded block text-amber-700 break-all">{tx.mfb_raw_response}</code>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                }) : (
                  <tr><td colSpan={8} className="px-3 py-12 text-center text-gray-400">
                    <TrendingDown className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    No payouts found{activeFilterCount > 0 ? ' matching your filters' : ' for this date'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer Stats */}
      {transactions.length > 0 && (
        <div className="text-xs text-gray-400 flex items-center justify-between">
          <span>{transactions.length} transfers{activeFilterCount > 0 ? ' (filtered)' : ''}</span>
          <span>{dateMode === 'all' ? 'All time' : dateMode === 'range' && endDate ? `${formatDateLabel(date)} - ${formatDateLabel(endDate)}` : formatDateLabel(date)}</span>
        </div>
      )}
    </div>
  );
}
